import {createListingContext, TListingTypes} from 'plugins/modules/listing/context';
import React, {ReactNode, useMemo} from 'react';
import {useSearchParams} from 'next/navigation';
import {TGetListingTransformedResponse} from 'plugins/modules/listing/types';
import {TGetListingRequest} from 'plugins/modules/listing/api/api';
import {useListing} from 'plugins/modules/listing/hooks/useListing';
import {useListingView} from 'plugins/modules/listing/hooks/useListingView';
import {useSortingControl} from 'hooks/product/useSortingControl';
import {
    ESortingActions,
    ESortingActions as ESortingActionsNew,
} from 'modules/products/providers/CatalogPageProvider/types';
import {TSortingList} from 'plugins/modules/sorting/types';
import {CookieService} from 'core/services/CookieService';
import {SORT_PARAM} from 'plugins/modules/sorting/constants';
import {useIsLoggedIn} from 'modules/user/hooks/useIsLoggedIn';

export interface IListingProviderProps<Request, Response> {
    children: ReactNode;
    initialData: Response;
    initialArgs: Request;
    lazyQuery: unknown;
    sortList: TSortingList;
    pageType: TListingTypes;
    dynamicQueryArgs?: Partial<Request>;
}

export const ListingProvider = <
    Product,
    Response extends TGetListingTransformedResponse<Product> = TGetListingTransformedResponse<Product>,
    Request extends TGetListingRequest = TGetListingRequest,
>({
    children,
    initialArgs,
    initialData,
    lazyQuery,
    sortList,
    pageType,
    dynamicQueryArgs,
}: IListingProviderProps<Request, Response>) => {
    const listing = useListing<Product, Response, Request>({
        initialData,
        lazyQuery,
        initialArgs,
        dynamicQueryArgs,
    });

    const {view, handleChangeView} = useListingView();

    const isLoggedIn = useIsLoggedIn();

    const searchParams = useSearchParams();

    const filteredSortList = useMemo(() => {
        if (!isLoggedIn) {
            return sortList.filter((sort) => sort.param !== ESortingActionsNew.PURCHASE);
        }
        return sortList;
    }, [sortList, isLoggedIn]);

    const defaultValue = useMemo(() => {
        // если в URL есть параметр сортировки не используем cookie
        const sortFromUrl = searchParams.get(SORT_PARAM);
        if (sortFromUrl) {
            return ESortingActions.OUR_CHOICE;
        }

        // если в URL нет параметра берем из cookie
        const cookieService = CookieService.getInstance();
        const sortFromCookie = cookieService.get('USER_SORT');

        if (sortFromCookie && filteredSortList.some((sort) => sort.param === sortFromCookie)) {
            return sortFromCookie;
        }

        return ESortingActions.OUR_CHOICE;
    }, [filteredSortList, searchParams]);

    const {sortingList} = useSortingControl({
        defaultValue,
        options: filteredSortList,
        sortingValues: ESortingActions,
    });

    const ListingContextProvider = createListingContext<Product>();

    return (
        <ListingContextProvider.Provider
            value={{
                ...listing,
                handleChangeView,
                sortingList,
                pageType,
                view,
            }}
        >
            {children}
        </ListingContextProvider.Provider>
    );
};
